import { XStack } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/lib/utils';

import type { DragEventHandler, MouseEvent, ReactNode } from 'react';

export function SelectableChatRow({
  id,
  label,
  isSelected,
  selectionActive,
  onToggleSelect,
  onOpen,
  children,
  className,
  draggable = false,
  onDragStart,
}: {
  id: string;
  label: string;
  isSelected: boolean;
  selectionActive: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (event: MouseEvent) => void;
  children: ReactNode;
  className?: string;
  draggable?: boolean;
  onDragStart?: DragEventHandler<HTMLDivElement>;
}) {
  const handleClick = (event: MouseEvent) => {
    if (selectionActive) {
      onToggleSelect(id);
      return;
    }
    onOpen(event);
  };

  return (
    <div className="group relative">
      <div
        className={cn(
          'absolute inset-y-0 right-full w-6',
          'flex items-center justify-center',
          'transition-opacity duration-150',
          selectionActive
            ? 'opacity-100'
            : 'opacity-0 touch:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100'
        )}
        onClick={(event) => {
          event.stopPropagation();
          if (event.target === event.currentTarget) onToggleSelect(id);
        }}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(id)}
          aria-label={`Select ${label}`}
        />
      </div>
      <XStack
        role="listitem"
        onClick={handleClick}
        draggable={draggable}
        onDragStart={onDragStart}
        className={cn(
          'items-start rounded-2xl px-3 py-3',
          'transition-colors group-hover:bg-surface-hover',
          isSelected && 'bg-surface-hover',
          className
        )}
      >
        {children}
      </XStack>
    </div>
  );
}
